import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { JobPhotoItem, PhotoUploadStatus } from '../../data/models/job-photo.models';
import { JobUploadSessionResponse, PrepareFileEntry, PreparedFileItem, LogUploadErrorRequest } from '../../data/models/job-image-upload.models';
import { JobProcessingStateService } from '../../data/services/job-processing-state.service';
import { JobsService } from '../../data/services/jobs.service';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import { CompaniesService } from '../../../companies/data/services/companies.service';
import { forkJoin } from 'rxjs';

/** Maximum number of concurrent Azure Blob PUT uploads. Higher values can cause browser/network instability with many Azure Blob PUTs. */
const CONCURRENT_UPLOADS = 4;

/** Number of files sent per prepare-files API call */
const PREPARE_BATCH_SIZE = 25;

@Component({
  selector: 'app-job-photos',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './job-photos.html',
  styleUrl: './job-photos.scss'
})
export class JobPhotos implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobsService = inject(JobsService);
  private readonly jobProcessingStateService = inject(JobProcessingStateService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly companiesService = inject(CompaniesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;
  readonly jobId = Number(this.route.snapshot.paramMap.get('id'));

  isLoading = false;
  isUploading = false;
  uploadProgress = 0;
  uploadStatusMessage = '';
  errorMessage = '';
  showErrorAlert = false;

  isSaving = false;
  isPaid = false;
  paymentStatus = '';
  upfrontCostOfJob = 0;
  jobStatus = '';

  get isTestCompany(): boolean {
    return this.selectedCompanyId() === 1;
  }

  get isPartiallyPaid(): boolean {
    const status = (this.paymentStatus || '').toLowerCase().replace(/[\s_]/g, '');
    return status === 'partiallypaid' || status === 'partialpaid';
  }

  get isFullyPaid(): boolean {
    const status = (this.paymentStatus || '').toLowerCase().replace(/[\s_]/g, '');
    return status === 'paid' || status === 'completed' || this.isPaid;
  }

  get isPaymentCompleted(): boolean {
    if (this.isTestCompany) {
      return true;
    }
    if (this.isFullyPaid) {
      return true;
    }
    // Partially paid users have already paid something — allow them to resume
    // uploading the remaining photos without paying the balance first.
    if (this.isPartiallyPaid) {
      return true;
    }
    if (this.upfrontCostOfJob <= 0) {
      return true;
    }
    return false;
  }

  get paymentStatusText(): string {
    if (this.isPaymentCompleted) {
      return this.isPartiallyPaid ? '✓ Partially Paid' : '✓ Paid';
    }
    return this.isPartiallyPaid ? 'Pay Remaining' : 'Pay Now';
  }

  jobUploadSessionId: number | null = null;
  uploadedCount = 0;
  duplicateCount = 0;
  failedCount = 0;
  autoRetryCount = 0;

  // ─── Multi-step Progress & Telemetry State ─────────────────────────────────
  uploadStep: 'idle' | 'preparing' | 'uploading' | 'completed' | 'error' = 'idle';
  uploadStepTitle = '';
  uploadStepMessage = '';
  prepareCurrent = 0;
  prepareTotal = 0;
  preparePercent = 0;
  uploadCurrent = 0;
  uploadTotal = 0;
  uploadPercent = 0;
  uploadSpeed = '';
  uploadEta = '';

  jobName = '';
  photos: JobPhotoItem[] = [];

  readonly pageSize = 20;
  currentPage = 1;
  private retryTimerId: any = null;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const sessionId = this.route.snapshot.queryParamMap.get('session_id');
    const isPaymentCanceled = this.route.snapshot.queryParamMap.get('payment_canceled');
    const isPopup = this.route.snapshot.queryParamMap.get('popup') === 'true';

    if (isPopup) {
      window.close();
      return;
    }

    if (sessionId && this.jobId) {
      this.verifyPaymentReturn(this.jobId, sessionId, isPopup);
    } else if (isPaymentCanceled) {
      this.errorMessage = 'Your Stripe checkout session was canceled.';
      if (isPopup) {
        window.close();
      }
    }

    this.loadJobDetails();
    this.loadAttachedFiles();
    this.checkActiveUploadSession();
  }

  ngOnDestroy(): void {
    this.clearRetryTimer();
  }

  // ─── Initialisation helpers ─────────────────────────────────────────────────

  private updateState(fn: () => void): void {
    this.ngZone.run(() => {
      fn();
      this.cdr.detectChanges();
    });
  }

  loadJobDetails(): void {
    this.updateState(() => {
      this.isLoading = true;
      this.errorMessage = '';
    });

    forkJoin({
      jobResponse: this.jobsService.getJobById(this.jobId),
      companyResponse: this.companiesService.getCompany(this.selectedCompanyId())
    }).subscribe({
      next: ({ jobResponse, companyResponse }) => {
        this.updateState(() => {
          this.jobStatus = jobResponse.status || '';
          this.isPaid = jobResponse.isPaid ?? false;
          this.paymentStatus = jobResponse.paymentStatus || '';
          
          const rawCost = jobResponse.upfrontCostOfJob || 0;
          const availableCredits = companyResponse.data?.availableCredits || 0;
          
          // Calculate the remaining amount to pay manually
          const remainingAmount = Math.max(0, rawCost - availableCredits);
          
          this.upfrontCostOfJob = Number(remainingAmount.toFixed(2));

          console.log('--- DEBUG PAYMENT STATE ---');
          console.log('rawCost (from jobResponse.upfrontCostOfJob):', rawCost);
          console.log('availableCredits (from companyResponse):', availableCredits);
          console.log('remainingAmount (assigned to this.upfrontCostOfJob):', this.upfrontCostOfJob);
          console.log('jobResponse.isPaid:', jobResponse.isPaid);
          console.log('jobResponse.paymentStatus:', jobResponse.paymentStatus);
          console.log('this.isPaid:', this.isPaid);
          console.log('this.paymentStatus:', this.paymentStatus);
          console.log('this.isPaymentCompleted getter:', this.isPaymentCompleted);
          console.log('---------------------------');

          this.jobProcessingStateService.setJobFlags({
            jobId: this.jobId,
            isEnhancements: jobResponse.enhanceImages,
            isCropping: jobResponse.cropImages,
            isBackgroundExtraction: jobResponse.extractBackground,
            isBestPick: jobResponse.pickBestImage
          });
          this.isLoading = false;
        });
      },
      error: () => {
        this.updateState(() => {
          this.errorMessage = 'Failed to load job details or company credits.';
          this.isLoading = false;
        });
      }
    });
  }

  loadAttachedFiles(): void {
    const cachedFiles = this.jobsService.getAttachedFiles();
    if (cachedFiles && cachedFiles.length > 0) {
      this.updateState(() => {
        this.photos = cachedFiles.map((file, index) => ({
          id: `attached-${Date.now()}-${index}`,
          clientFileId: crypto.randomUUID(),
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
          file,
          isSelected: false,
          uploadStatus: 'pending' as PhotoUploadStatus,
          uploadProgress: 0
        }));
        this.recalculateSummaryInternal();
      });
    }
  }

  checkActiveUploadSession(): void {
    this.updateState(() => {
      this.isLoading = true;
    });
    this.jobsService.getActiveJobUploadSession(this.jobId, this.selectedCompanyId()).subscribe({
      next: (res) => {
        if (res.data) {
          this.updateState(() => {
            this.jobUploadSessionId = res.data.jobUploadSessionId;
            this.restoreSessionCounts(res.data);
          });
          // Immediately reflect server state in the UI — don't wait for user to click Start Upload
          this.reconcileOnPageLoad(res.data.jobUploadSessionId);
        } else {
          this.updateState(() => {
            this.isLoading = false;
            this.recalculateSummaryInternal();
          });
        }
      },
      error: (err: HttpErrorResponse) => {
        this.updateState(() => {
          this.isLoading = false;
          this.recalculateSummaryInternal();
        });
        if (err.status !== 404) {
          this.handleError('Unable to check existing upload session. Please try again.');
        }
      }
    });
  }

  /**
   * Called once on page load when an active session is found.
   * Calls GET pending-files to get the authoritative server view and patches
   * local photo statuses immediately — confirmed files are marked so the user
   * sees accurate state before clicking anything.
   */
  private reconcileOnPageLoad(sessionId: number): void {
    this.jobsService.getPendingFiles(this.jobId, sessionId).subscribe({
      next: (res) => {
        const pendingItems = res.data?.items ?? [];

        // Build fast-lookup maps by all three identifiers
        const byItemId  = new Map(pendingItems.map(i => [i.jobUploadSessionItemId, i]));
        const byClientId = new Map(pendingItems.map(i => [i.clientFileId, i]));
        const byName    = new Map(pendingItems.map(i => [(i.originalFileName || '').toLowerCase().trim(), i]));

        this.updateState(() => {
          this.photos.forEach(p => {
            const serverItem =
              byItemId.get(p.jobUploadSessionItemId ?? -1) ??
              byClientId.get(p.clientFileId ?? '') ??
              byName.get((p.fileName || '').toLowerCase().trim());

            if (serverItem) {
              // Still unconfirmed on server — patch with server data and preserve status
              p.jobUploadSessionItemId = serverItem.jobUploadSessionItemId;
              p.uploadUrl             = serverItem.uploadUrl ?? null;
              p.uploadUrlExpiresAt    = serverItem.uploadUrlExpiresAt ?? null;
              // Map server status to local status
              const s = serverItem.status.toLowerCase();
              p.uploadStatus = (s === 'failed' ? 'failed' : 'pending') as PhotoUploadStatus;
            } else {
              // Not in pending list → already confirmed on the server
              p.uploadStatus  = 'confirmed';
              p.uploadProgress = 100;
            }
          });

          this.isLoading = false;
          this.recalculateSummaryInternal();
        });
      },
      error: () => {
        this.updateState(() => {
          // Non-fatal: fall back to showing all files as pending
          this.isLoading = false;
          this.recalculateSummaryInternal();
        });
      }
    });
  }

  // ─── Paging helpers ─────────────────────────────────────────────────────────

  get totalCount(): number { return this.photos.length; }
  get totalPages(): number { return Math.max(1, Math.ceil(this.totalCount / this.pageSize)); }
  get pagedPhotos(): JobPhotoItem[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.photos.slice(start, start + this.pageSize);
  }
  get startIndex(): number { return (this.currentPage - 1) * this.pageSize + 1; }
  get endIndex(): number { return Math.min(this.currentPage * this.pageSize, this.totalCount); }

  goToPrevPage(): void { if (this.currentPage > 1) this.currentPage--; }
  goToNextPage(): void { if (this.currentPage < this.totalPages) this.currentPage++; }

  // ─── Derived counts ─────────────────────────────────────────────────────────

  /** Files that the Start/Resume Upload button will process (pending + failed not yet confirmed) */
  get pendingCount(): number {
    return this.photos.filter(
      p => p.uploadStatus === 'pending' || p.uploadStatus === 'failed'
    ).length;
  }

  get localFailedCount(): number {
    return this.photos.filter(p => p.uploadStatus === 'failed').length;
  }

  get confirmedCount(): number {
    return this.photos.filter(p => p.uploadStatus === 'confirmed').length;
  }

  get skippedCount(): number {
    return this.photos.filter(
      p => p.uploadStatus === 'skipped' || p.uploadStatus === 'duplicate_skipped'
    ).length;
  }

  get isAllUploaded(): boolean {
    return this.photos.length > 0 && this.pendingCount === 0 && (this.confirmedCount + this.skippedCount === this.photos.length);
  }

  get overallProgress(): number {
    if (this.photos.length === 0) return 0;
    const done = this.confirmedCount + this.skippedCount;
    return Math.floor((done / this.photos.length) * 100);
  }

  /**
   * Recalculates all reactive summary properties and triggers manual change detection
   * to ensure OnPush components update instantly.
   */
  recalculateSummary(): void {
    this.updateState(() => {
      this.recalculateSummaryInternal();
    });
  }

  private recalculateSummaryInternal(): void {
    const total = this.photos.length;
    this.uploadedCount = this.confirmedCount;
    this.duplicateCount = this.skippedCount;
    this.failedCount = this.localFailedCount;

    if (total > 0) {
      const done = this.uploadedCount + this.duplicateCount;
      this.uploadProgress = Math.floor((done / total) * 100);
      this.uploadCurrent = done;
      this.uploadPercent = this.uploadProgress;
    } else {
      this.uploadProgress = 0;
      this.uploadCurrent = 0;
      this.uploadPercent = 0;
    }
  }

  // ─── Session count sync ─────────────────────────────────────────────────────

  restoreSessionCounts(data: JobUploadSessionResponse): void {
    this.uploadedCount = data.uploadedFileCount ?? 0;
    this.duplicateCount = data.duplicateFileCount ?? 0;
    this.failedCount = data.failedFileCount ?? 0;
  }

  // ─── Public actions ─────────────────────────────────────────────────────────

  verifyPaymentReturn(jobId: number, sessionId: string, isPopup: boolean = false): void {
    this.isLoading = true;
    this.jobsService.verifyPaymentStatus(jobId, sessionId).subscribe({
      next: (res) => {
        if (res.success && res.isPaid) {
          this.updateState(() => {
            this.isPaid = true;
            this.paymentStatus = 'Paid';
            this.upfrontCostOfJob = 0;
            this.errorMessage = '';
          });
          if (isPopup) {
            window.close();
          }
          this.onStartUpload();
        } else {
          this.errorMessage = 'Failed to verify payment status with Stripe.';
        }
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Error occurred during Stripe payment verification.';
        this.isLoading = false;
      }
    });
  }

  private pollPaymentStatus(jobId: number, sessionId: string, paymentWindow: Window): void {
    if (!sessionId) {
      return;
    }

    const pollInterval = setInterval(() => {
      if (paymentWindow.closed) {
        clearInterval(pollInterval);
        this.updateState(() => {
          this.isSaving = false;
        });
        // Verify one last time in case they successfully paid right before closing
        this.jobsService.verifyPaymentStatus(jobId, sessionId).subscribe({
          next: (res) => {
            if (res.success && res.isPaid) {
              this.updateState(() => {
                this.isPaid = true;
                this.paymentStatus = 'Paid';
                this.upfrontCostOfJob = 0;
              });
              this.onStartUpload();
            }
          }
        });
        return;
      }

      this.jobsService.verifyPaymentStatus(jobId, sessionId).subscribe({
        next: (res) => {
          if (res.success && res.isPaid) {
            clearInterval(pollInterval);
            if (!paymentWindow.closed) {
              paymentWindow.close();
            }
            this.updateState(() => {
              this.isPaid = true;
              this.paymentStatus = 'Paid';
              this.upfrontCostOfJob = 0;
              this.errorMessage = '';
              this.isSaving = false;
            });
            this.onStartUpload();
          }
        }
      });
    }, 3000);
  }

  onPayNow(): void {
    this.updateState(() => {
      this.isSaving = true;
      this.errorMessage = '';
    });

    const openPaymentPopup = (): Window | null => {
      const popupWidth = 600;
      const popupHeight = 700;
      const left = window.screen ? (window.screen.width - popupWidth) / 2 : 0;
      const top = window.screen ? (window.screen.height - popupHeight) / 2 : 0;
      const win = window.open('', 'StripeCheckout', `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`);
      if (win) {
        win.document.write('Loading secure payment gateway...');
      }
      return win;
    };

    const processPaymentForJob = (jobId: number) => {
      const returnBase = window.location.origin + '/app/jobs/' + jobId + '/photos';
      const successUrl = returnBase + '?session_id={CHECKOUT_SESSION_ID}&popup=true';
      const cancelUrl = returnBase + '?payment_canceled=true&popup=true';
      const amountToPay = this.upfrontCostOfJob;

      this.jobsService.createCheckoutSession(jobId, amountToPay, successUrl, cancelUrl).subscribe({
        next: (res) => {
          const isSkipped =
            res.paymentSkipped === true ||
            res.requiresPayment === false ||
            (!res.checkoutUrl && (res.paymentSkipped !== undefined || res.requiresPayment !== undefined));

          if (isSkipped) {
            this.updateState(() => {
              this.isPaid = true;
              this.paymentStatus = 'Paid';
              this.upfrontCostOfJob = 0;
              this.uploadStatusMessage = 'Payment skipped for owner company.';
              this.isSaving = false;
            });
            this.onStartUpload();
            return;
          }

          if (res.checkoutUrl) {
            const paymentWindow = openPaymentPopup();
            if (paymentWindow) {
              paymentWindow.location.href = res.checkoutUrl;
              this.pollPaymentStatus(jobId, res.sessionId || '', paymentWindow);
            } else {
              window.location.href = res.checkoutUrl;
            }
          } else {
            this.updateState(() => {
              this.errorMessage = 'Stripe redirect error: Missing checkout session URL from server.';
              this.isSaving = false;
            });
          }
        },
        error: () => {
          this.updateState(() => {
            this.errorMessage = 'Failed to initialize Stripe checkout session.';
            this.isSaving = false;
          });
        }
      });
    };

    processPaymentForJob(this.jobId);
  }

  onStartUpload(): void {
    // When the job status is already ImageUploadCompleted the images have been
    // uploaded in a previous session. Skip the SAS upload flow and directly
    // submit the job for processing.
    if (this.jobStatus === 'ImageUploadCompleted') {
      this.finalizeUpload();
      return;
    }

    // When all images are already confirmed or skipped, finalize the session and submit for processing
    if (this.isAllUploaded) {
      this.onCompleteUpload();
      return;
    }

    // Include locally-'failed' files here too: after page-load reconciliation, files
    // the server still considers pending/failed are set to 'failed' locally, so "Start Upload"
    // acts as a true resume — uploading everything the server hasn't confirmed yet.
    const filesToUpload = this.photos.filter(
      p => (p.uploadStatus === 'pending' || p.uploadStatus === 'failed') && p.file
    );
    if (filesToUpload.length === 0) {
      this.handleError('Please select at least one image.');
      return;
    }
    this.clearRetryTimer();
    this.autoRetryCount = 0;
    this.runSasUploadFlow(filesToUpload);
  }

  onRetryFailed(): void {
    this.clearRetryTimer();
    this.autoRetryCount = 0;
    this.runRetryFlow();
  }

  private async runRetryFlow(): Promise<void> {
    if (!this.jobUploadSessionId) {
      this.handleError('Upload session is missing. Please restart the upload.');
      return;
    }

    const localFailedOrPending = this.photos.filter(
      p => (p.uploadStatus === 'failed' || p.uploadStatus === 'pending') && p.file
    );
    if (localFailedOrPending.length === 0) {
      this.handleError('No failed or pending files to retry.');
      return;
    }

    this.updateState(() => {
      this.isUploading = true;
      this.uploadStatusMessage = this.autoRetryCount > 0
        ? `Retrying failed uploads (attempt ${this.autoRetryCount}/3)…`
        : 'Verifying upload status with server…';
      this.recalculateSummaryInternal();
    });

    try {
      // Step 1 — Ask the server which items are genuinely still unconfirmed.
      // This is the authoritative list: anything NOT in this list is already confirmed.
      const pendingRes = await firstValueFrom(
        this.jobsService.getPendingFiles(this.jobId, this.jobUploadSessionId)
      );
      const serverPendingItems = pendingRes.data?.items ?? [];

      // Build a fast-lookup by item ID and filename for server-side pending set
      const serverPendingByItemId = new Map(
        serverPendingItems.map(i => [i.jobUploadSessionItemId, i])
      );
      const serverPendingByClientId = new Map(
        serverPendingItems.map(i => [i.clientFileId, i])
      );
      const serverPendingByName = new Map(
        serverPendingItems.map(i => [i.originalFileName, i])
      );

      // Step 2 — Reconcile local state against the server truth.
      // Any locally-failed file that the server does NOT list as pending,
      // AND which has been prepared (has jobUploadSessionItemId),
      // is already confirmed on the server — mark it confirmed locally.
      this.updateState(() => {
        this.photos.forEach(p => {
          if (p.uploadStatus === 'confirmed' || p.uploadStatus === 'skipped' || p.uploadStatus === 'duplicate_skipped') {
            return; // Already done — never touch these
          }

          // Only reconcile files that were actually prepared/registered on the server.
          // If they don't have jobUploadSessionItemId, they were never prepared successfully.
          if (!p.jobUploadSessionItemId) {
            return;
          }

          const serverItem =
            serverPendingByItemId.get(p.jobUploadSessionItemId) ??
            serverPendingByClientId.get(p.clientFileId ?? '') ??
            serverPendingByName.get(p.fileName);

          if (!serverItem) {
            // Server does not list this as pending → it was confirmed elsewhere
            p.uploadStatus = 'confirmed';
            p.uploadProgress = 100;
          }
        });

        this.recalculateSummaryInternal();
      });

      // Step 3 — Determine the files that genuinely still need uploading
      const needsRetry = this.photos.filter(
        p => (p.uploadStatus === 'failed' || p.uploadStatus === 'pending') && p.file
      );

      if (needsRetry.length === 0) {
        // Everything is confirmed — go to complete
        this.updateState(() => {
          this.isUploading = false;
        });
        this.onCompleteUpload();
        return;
      }

      // Separate retry files into those that need prepare vs those that only need SAS url refresh
      const needsPrepare = needsRetry.filter(p => !p.jobUploadSessionItemId);
      const needsRefresh = needsRetry.filter(p => !!p.jobUploadSessionItemId);

      // Step 4 — Prepare files that haven't been registered on the server yet
      if (needsPrepare.length > 0) {
        this.updateState(() => {
          this.uploadStep = 'preparing';
          this.prepareTotal = needsPrepare.length;
          this.prepareCurrent = 0;
          this.preparePercent = 0;
          this.uploadStatusMessage = this.autoRetryCount > 0
            ? `Preparing unregistered files (attempt ${this.autoRetryCount}/3)…`
            : 'Preparing unregistered files…';
        });
        await this.prepareFilesInBatches(needsPrepare);
      }

      // Step 5 — Refresh SAS URLs for already prepared files
      if (needsRefresh.length > 0) {
        this.updateState(() => {
          this.uploadStatusMessage = this.autoRetryCount > 0
            ? `Refreshing upload URLs (attempt ${this.autoRetryCount}/3)…`
            : 'Refreshing upload URLs…';
        });
        try {
          const refreshRes = await firstValueFrom(
            this.jobsService.refreshUploadUrls(this.jobId, this.jobUploadSessionId)
          );
          const refreshedItems = refreshRes.data?.items ?? [];

          // Patch each retry candidate with a fresh SAS URL from the server
          this.updateState(() => {
            needsRefresh.forEach(photo => {
              const refreshed = refreshedItems.find(
                i => i.jobUploadSessionItemId === photo.jobUploadSessionItemId
                  || i.clientFileId === photo.clientFileId
                  || i.originalFileName === photo.fileName
              );

              if (refreshed?.uploadUrl) {
                photo.uploadUrl = refreshed.uploadUrl;
                photo.uploadUrlExpiresAt = refreshed.uploadUrlExpiresAt ?? null;
                photo.jobUploadSessionItemId ??= refreshed.jobUploadSessionItemId;
                photo.uploadStatus = 'pending';
                photo.uploadProgress = 0;
                photo.errorMessage = null;
              } else {
                photo.uploadStatus = 'failed';
                photo.errorMessage = 'Could not obtain a fresh upload URL from the server.';
              }
            });
          });
        } catch (err: any) {
          this.updateState(() => {
            needsRefresh.forEach(photo => {
              photo.uploadStatus = 'failed';
              photo.errorMessage = 'Failed to refresh upload URL.';
            });
          });
          // Log error but don't fail the entire retry flow because some files (in needsPrepare) might still be ready.
          for (const p of needsRefresh) {
            await this.logUploadError(
              p,
              'REFRESH_URLS_FAILED',
              err?.message || 'Failed to refresh upload URL.',
              `Refresh API failed: ${err?.stack || err?.toString()}`
            );
          }
        }
      }

      // Step 6 — Queue only files that now have a valid URL and are pending
      const readyToUpload = needsRetry.filter(
        p => p.uploadStatus === 'pending' && p.uploadUrl && p.file
      );

      if (readyToUpload.length === 0) {
        const stillFailed = this.photos.filter(p => p.uploadStatus === 'failed').length;
        if (stillFailed > 0) {
          this.handleQueueComplete();
          return;
        }
        this.updateState(() => {
          this.isUploading = false;
        });
        this.handleError('No files could be prepared for retry. Please contact support.');
        return;
      }

      this.updateState(() => {
        this.uploadStep = 'uploading';
        this.uploadTotal = this.photos.length;
        this.uploadCurrent = this.confirmedCount + this.duplicateCount;
        this.uploadPercent = Math.min(100, Math.floor((this.uploadCurrent / Math.max(1, this.uploadTotal)) * 100));
      });

      await this.runUploadQueue(readyToUpload);

    } catch (err: any) {
      console.error('runRetryFlow error during execution:', err);
      this.updateState(() => {
        localFailedOrPending.forEach(p => {
          if (p.uploadStatus === 'pending' || p.uploadStatus === 'preparing' || p.uploadStatus === 'uploading' || p.uploadStatus === 'confirming' || p.uploadStatus === 'failed') {
            p.uploadStatus = 'failed';
            p.errorMessage = err?.message || 'Failed to verify or refresh upload session with server.';
          }
        });
        this.recalculateSummaryInternal();
      });
      this.handleQueueComplete();
    }
  }

  onCompleteUpload(): void {
    if (!this.jobUploadSessionId) {
      this.finalizeUpload();
      return;
    }
    this.updateState(() => {
      this.isUploading = true;
      this.uploadStatusMessage = 'Finalising session…';
    });

    this.jobsService.completeJobUploadSession(this.jobId, this.jobUploadSessionId, this.selectedCompanyId()).subscribe({
      next: () => {
        this.updateState(() => {
          this.uploadStatusMessage = 'Submitting job for processing…';
        });
        this.finalizeUpload();
      },
      error: () => {
        this.updateState(() => {
          this.isUploading = false;
          this.recalculateSummaryInternal();
        });
        this.handleError('Upload finished, but the session could not be finalised. Please try again.');
      }
    });
  }

  // ─── Core SAS flow ──────────────────────────────────────────────────────────

  /**
   * Entry point for a fresh upload or resume.
   * 1. Start or resume session
   * 2. Compute SHA-256 hashes non-blockingly with event-loop yields
   * 3. prepare-files in batches of 25
   * 4. Pipelined background upload to Azure Blob with bounded concurrency (4 parallel streams)
   * 5. confirm-file per uploaded file
   * 6. complete session
   */
  private async runSasUploadFlow(filesToProcess: JobPhotoItem[]): Promise<void> {
    this.updateState(() => {
      this.isUploading = true;
      this.uploadStep = 'preparing';
      this.uploadStepTitle = 'Preparing Your Images';
      this.uploadStepMessage = 'Starting upload session…';
      this.prepareTotal = filesToProcess.length;
      this.prepareCurrent = 0;
      this.preparePercent = 0;
      this.uploadTotal = this.photos.length;
      this.uploadCurrent = this.confirmedCount + this.duplicateCount;
      this.uploadPercent = Math.min(100, Math.floor((this.uploadCurrent / Math.max(1, this.uploadTotal)) * 100));
      this.uploadSpeed = '';
      this.uploadEta = '';
      this.errorMessage = '';
      this.recalculateSummaryInternal();
    });

    try {
      // ── Step 1: start-or-resume ────────────────────────────────────────────
      const sessionRes = await firstValueFrom(
        this.jobsService.startOrResumeUploadSession(this.jobId, {
          companyId: this.selectedCompanyId(),
          expectedFileCount: this.photos.length
        })
      );

      if (!sessionRes.data) {
        throw new Error(sessionRes.message || 'Could not start upload session.');
      }

      this.updateState(() => {
        this.jobUploadSessionId = sessionRes.data.jobUploadSessionId;
        this.restoreSessionCounts(sessionRes.data);
      });

      // If the session was resumed, reconcile confirmed files
      if (sessionRes.data.resumed) {
        await this.reconcileResumedSession(filesToProcess);
      }

      // Filter out anything already confirmed or skipped by a prior session
      const uploadQueueList = filesToProcess.filter(
        p => p.uploadStatus !== 'confirmed' && p.uploadStatus !== 'skipped' && p.uploadStatus !== 'duplicate_skipped'
      );

      if (uploadQueueList.length === 0) {
        this.uploadStep = 'completed';
        this.onCompleteUpload();
        return;
      }

      this.updateState(() => {
        this.prepareTotal = uploadQueueList.length;
        this.prepareCurrent = 0;
        this.preparePercent = 0;
        this.uploadStatusMessage = 'Preparing files…';
        uploadQueueList.forEach(p => p.uploadStatus = 'preparing');
        this.recalculateSummaryInternal();
      });

      // Pipelined background upload queue consumer
      const uploadQueue: JobPhotoItem[] = [];
      let isPreparingDone = false;
      let activeUploadCount = 0;
      let totalBytesUploaded = 0;
      const uploadStartTime = Date.now();
      let filesUploadedThisRun = 0;

      let queueResolver: (() => void) | null = null;
      const queueDonePromise = new Promise<void>((res) => { queueResolver = res; });

      const processUploadQueue = async () => {
        while (true) {
          if (!this.isUploading) {
            uploadQueue.length = 0;
            if (queueResolver) queueResolver();
            break;
          }

          if (uploadQueue.length === 0) {
            if (isPreparingDone && activeUploadCount === 0) {
              if (queueResolver) queueResolver();
              break;
            }
            await new Promise((r) => setTimeout(r, 60));
            continue;
          }

          while (activeUploadCount < CONCURRENT_UPLOADS && uploadQueue.length > 0) {
            if (!this.isUploading) {
              uploadQueue.length = 0;
              break;
            }

            const photo = uploadQueue.shift()!;
            activeUploadCount++;

            this.uploadOneFile(photo)
              .then(() => {
                this.updateState(() => {
                  activeUploadCount--;
                  filesUploadedThisRun++;
                  totalBytesUploaded += photo.file?.size ?? 0;

                  const elapsedSec = (Date.now() - uploadStartTime) / 1000;
                  if (elapsedSec > 1 && totalBytesUploaded > 0) {
                    const speedBps = totalBytesUploaded / elapsedSec;
                    this.uploadSpeed = `${(speedBps / (1024 * 1024)).toFixed(1)} MB/s`;

                    const remainingFiles = Math.max(0, this.uploadTotal - this.uploadCurrent);
                    const avgBytes = totalBytesUploaded / Math.max(1, filesUploadedThisRun);
                    const etaSec = Math.round((remainingFiles * avgBytes) / speedBps);
                    this.uploadEta = etaSec > 60 ? `~${Math.ceil(etaSec / 60)} min left` : `~${etaSec}s left`;
                  }

                  this.recalculateSummaryInternal();
                });
              })
              .catch((err) => {
                console.error(`Failed uploading ${photo.fileName}:`, err);
                this.updateState(() => {
                  activeUploadCount--;
                  this.recalculateSummaryInternal();
                });
              });
          }

          await new Promise((r) => setTimeout(r, 40));
        }
      };

      const consumerPromise = processUploadQueue();

      // Run preparation and feed ready items directly to uploadQueue as chunks are prepared
      await this.prepareFilesInBatches(uploadQueueList, (chunk) => {
        const ready = chunk.filter(p => p.uploadStatus === 'pending' && p.uploadUrl);
        if (ready.length > 0) {
          this.updateState(() => {
            this.uploadStep = 'uploading';
          });
          uploadQueue.push(...ready);
        }
      });

      isPreparingDone = true;
      await queueDonePromise;
      await consumerPromise;

      this.handleQueueComplete();

    } catch (err: any) {
      this.updateState(() => {
        this.isUploading = false;
        this.uploadStep = 'error';
        this.recalculateSummaryInternal();
      });
      this.handleError(err?.message ?? 'Upload failed. Please try again.');
    }
  }

  /**
   * When resuming a session, call pending-files and mark already-confirmed
   * local photos so they are skipped.
   */
  private async reconcileResumedSession(localPhotos: JobPhotoItem[]): Promise<void> {
    if (!this.jobUploadSessionId) return;

    try {
      const res = await firstValueFrom(
        this.jobsService.getPendingFiles(this.jobId, this.jobUploadSessionId)
      );

      // Build a set of clientFileIds / fileNames that are still pending
      const pendingIds = new Set(res.data?.items.map(i => i.clientFileId) ?? []);
      const pendingNames = new Set(res.data?.items.map(i => i.originalFileName) ?? []);

      this.updateState(() => {
        localPhotos.forEach(p => {
          const isPending = pendingIds.has(p.clientFileId ?? '') || pendingNames.has(p.fileName);
          if (!isPending) {
            // Not pending on the server → already confirmed
            p.uploadStatus = 'confirmed';
            p.uploadProgress = 100;
          }
        });
        this.recalculateSummaryInternal();
      });
    } catch {
      // Non-fatal — if pending-files fails we still attempt to upload
    }
  }

  /**
   * Sends file metadata to prepare-files in chunks of PREPARE_BATCH_SIZE.
   * Hashes files sequentially with explicit event-loop yields to eliminate UI freezing.
   * Optionally triggers onBatchPrepared as each batch completes.
   */
  private async prepareFilesInBatches(
    photos: JobPhotoItem[],
    onBatchPrepared?: (batch: JobPhotoItem[]) => void
  ): Promise<JobPhotoItem[]> {
    if (!this.jobUploadSessionId) return photos;

    const chunks = this.chunk(photos, PREPARE_BATCH_SIZE);
    const sessionId = this.jobUploadSessionId;

    for (const chunk of chunks) {
      if (!this.isUploading) break;

      this.updateState(() => {
        chunk.forEach(p => p.uploadStatus = 'preparing');
        this.recalculateSummaryInternal();
      });

      // Hash files sequentially with yields to keep browser animations and UI fluid
      for (const p of chunk) {
        if (!this.isUploading) break;
        if (!p.fileHash && p.file) {
          p.fileHash = await this.computeSha256Hex(p.file);
        }
        this.updateState(() => {
          this.prepareCurrent++;
          this.preparePercent = this.prepareTotal > 0
            ? Math.min(100, Math.floor((this.prepareCurrent / this.prepareTotal) * 100))
            : 0;
          this.uploadStatusMessage = `Preparing images: ${this.prepareCurrent} of ${this.prepareTotal} (${this.preparePercent}%)…`;
        });
        // Yield to event loop to keep UI thread completely responsive
        await new Promise((r) => setTimeout(r, 0));
      }

      if (!this.isUploading) break;

      const entries: PrepareFileEntry[] = chunk.map(p => ({
        clientFileId: p.clientFileId!,
        originalFileName: p.fileName,
        fileHash: p.fileHash ?? undefined,
        fileSizeBytes: p.file?.size,
        contentType: p.file?.type || 'image/jpeg'
      }));

      let prepareRes;
      try {
        prepareRes = await firstValueFrom(
          this.jobsService.prepareFiles(this.jobId, sessionId, {
            companyId: this.selectedCompanyId(),
            files: entries
          })
        );
      } catch (err: any) {
        // Mark whole batch as failed on network error
        this.updateState(() => {
          chunk.forEach(p => {
            p.uploadStatus = 'failed';
            p.errorMessage = 'Failed to prepare file for upload.';
          });
          this.recalculateSummaryInternal();
        });

        // Log error via logUploadError for each item in the chunk
        for (const p of chunk) {
          await this.logUploadError(
            p,
            'PREPARE_FILES_FAILED',
            err?.message || 'Failed to prepare file for upload.',
            `Chunk prepare API failed: ${err?.stack || err?.toString()}`
          );
        }
        continue;
      }

      const failedToPrepareReason: { photo: JobPhotoItem; code: string; message: string; details: string }[] = [];

      this.updateState(() => {
        // Apply server response back to local photo objects
        prepareRes.data?.items.forEach((serverItem: PreparedFileItem) => {
          const photo = chunk.find(
            p => p.clientFileId === serverItem.clientFileId
              || p.fileName === serverItem.originalFileName
          );
          if (!photo) return;

          photo.jobUploadSessionItemId = serverItem.jobUploadSessionItemId;
          photo.uploadUrl = serverItem.uploadUrl ?? null;
          photo.uploadUrlExpiresAt = serverItem.uploadUrlExpiresAt ?? null;

          if (serverItem.alreadyUploaded || serverItem.status === 'Uploaded' || serverItem.status === 'Confirmed') {
            photo.uploadStatus = 'skipped';
            photo.jobImageId = serverItem.jobImageId ?? null;
            photo.errorMessage = null;
          } else if (serverItem.uploadUrl) {
            photo.uploadStatus = 'pending';
            photo.errorMessage = null;
          } else {
            photo.uploadStatus = 'failed';
            photo.errorMessage = 'No upload URL returned from server.';
            failedToPrepareReason.push({
              photo,
              code: 'PREPARE_NO_URL',
              message: 'No upload URL returned from server.',
              details: `Server status: ${serverItem.status}`
            });
          }
        });

        // Mark other match/unmatch items appropriately (ensure no stale prepare logic leaves status at preparing)
        chunk.forEach(p => {
          if (p.uploadStatus === 'preparing') {
            p.uploadStatus = 'failed';
            p.errorMessage = 'Not returned in prepare response.';
            failedToPrepareReason.push({
              photo: p,
              code: 'PREPARE_NOT_RETURNED',
              message: 'Not returned in prepare response.',
              details: 'The server did not include this file in the prepare-files response.'
            });
          }
        });

        this.recalculateSummaryInternal();
      });

      // Log any failures to the backend asynchronously
      for (const item of failedToPrepareReason) {
        await this.logUploadError(item.photo, item.code, item.message, item.details);
      }

      if (onBatchPrepared) {
        onBatchPrepared(chunk);
      }
    }

    return photos;
  }

  /**
   * Uploads each photo directly to Azure Blob Storage using its SAS URL,
   * then calls confirm-file. Uses a bounded concurrency pool (CONCURRENT_UPLOADS).
   */
  private runUploadQueue(photos: JobPhotoItem[]): Promise<void> {
    return new Promise<void>((resolve) => {
      const queue = [...photos];
      let active = 0;
      let totalBytesUploaded = 0;
      const uploadStartTime = Date.now();
      let filesUploadedThisRun = 0;

      const startNext = () => {
        if (queue.length === 0 && active === 0) {
          // All done
          this.updateState(() => {
            this.recalculateSummaryInternal();
          });
          this.handleQueueComplete();
          resolve();
          return;
        }

        while (active < CONCURRENT_UPLOADS && queue.length > 0) {
          const photo = queue.shift()!;
          active++;
          this.uploadOneFile(photo).then(() => {
            active--;
            filesUploadedThisRun++;
            totalBytesUploaded += photo.file?.size ?? 0;

            const elapsedSec = (Date.now() - uploadStartTime) / 1000;
            if (elapsedSec > 1 && totalBytesUploaded > 0) {
              const speedBps = totalBytesUploaded / elapsedSec;
              this.uploadSpeed = `${(speedBps / (1024 * 1024)).toFixed(1)} MB/s`;

              const remainingFiles = Math.max(0, this.uploadTotal - this.uploadCurrent);
              const avgBytes = totalBytesUploaded / Math.max(1, filesUploadedThisRun);
              const etaSec = Math.round((remainingFiles * avgBytes) / speedBps);
              this.uploadEta = etaSec > 60 ? `~${Math.ceil(etaSec / 60)} min left` : `~${etaSec}s left`;
            }

            this.updateState(() => {
              this.recalculateSummaryInternal();
            });
            startNext();
          });
        }
      };

      startNext();
    });
  }

  /**
   * Uploads a single file to Azure Blob Storage and then confirms it.
   * Retries up to 3 attempts with exponential backoff on failures.
   */
  private async uploadOneFile(photo: JobPhotoItem): Promise<void> {
    if (!photo.file || !photo.uploadUrl) {
      this.updateState(() => {
        photo.uploadStatus = 'failed';
        photo.errorMessage = 'Missing file or upload URL.';
        this.recalculateSummaryInternal();
      });
      return;
    }

    let attempts = 0;
    const maxAttempts = 3;
    let success = false;

    while (attempts < maxAttempts && !success) {
      attempts++;

      // Check SAS expiry before uploading
      if (photo.uploadUrlExpiresAt) {
        const expiresAt = new Date(photo.uploadUrlExpiresAt);
        if (expiresAt <= new Date()) {
          // Expired — try to refresh
          const refreshed = await this.refreshSasForPhoto(photo);
          if (!refreshed) {
            const errMsg = 'Upload URL expired and could not be refreshed.';
            await this.logUploadError(photo, 'REFRESH_SAS_FAILED', errMsg, `Attempt ${attempts}`);
            if (attempts >= maxAttempts) {
              this.updateState(() => {
                photo.uploadStatus = 'failed';
                photo.errorMessage = errMsg;
                this.recalculateSummaryInternal();
              });
              return;
            }
            await this.delay(this.getRetryDelay(attempts - 1));
            continue;
          }
        }
      }

      this.updateState(() => {
        photo.uploadStatus = 'uploading';
        photo.uploadProgress = 0;
        this.recalculateSummaryInternal();
      });

      // PUT to Azure Blob Storage with progress tracking
      const uploadResult = await this.putToAzureBlob(
        photo.uploadUrl,
        photo.file,
        (pct) => {
          this.updateState(() => {
            photo.uploadProgress = pct;
          });
        }
      );

      if (!uploadResult.success) {
        const errorCode = uploadResult.errorCode || 'AZURE_UPLOAD_FAILED';
        const errorMessage = uploadResult.errorMessage || 'Failed to upload file to Azure storage.';
        const errorDetails = uploadResult.errorDetails || `Attempt ${attempts} / status code: ${uploadResult.statusCode}`;

        // Log this attempt's error
        await this.logUploadError(photo, errorCode, errorMessage, errorDetails);

        if (attempts >= maxAttempts) {
          this.updateState(() => {
            photo.uploadStatus = 'failed';
            photo.errorMessage = errorMessage;
            this.recalculateSummaryInternal();
          });
          return;
        }

        await this.delay(this.getRetryDelay(attempts - 1));
        continue;
      }

      this.updateState(() => {
        photo.uploadStatus = 'uploadedToBlob';
        photo.uploadProgress = 100;
        this.recalculateSummaryInternal();
      });

      // Confirm with the API
      this.updateState(() => {
        photo.uploadStatus = 'confirming';
        this.recalculateSummaryInternal();
      });

      const confirmRes = await this.confirmOneFile(photo);
      if (!confirmRes.success) {
        const errorCode = 'CONFIRM_FILE_FAILED';
        const errorMessage = confirmRes.errorMessage || 'Failed to confirm upload.';
        const errorDetails = confirmRes.errorDetails || `Attempt ${attempts}`;

        // Log this attempt's error
        await this.logUploadError(photo, errorCode, errorMessage, errorDetails);

        if (attempts >= maxAttempts) {
          this.updateState(() => {
            photo.uploadStatus = 'failed';
            photo.errorMessage = errorMessage;
            this.recalculateSummaryInternal();
          });
          return;
        }

        await this.delay(this.getRetryDelay(attempts - 1));
        continue;
      }

      // Succeeded!
      success = true;
      this.updateState(() => {
        photo.uploadStatus = 'confirmed';
        photo.jobImageId = confirmRes.jobImageId ?? null;
        photo.errorMessage = null;
        this.recalculateSummaryInternal();
      });
    }
  }

  /**
   * Calls confirm-file for a single photo.
   * Returns success status, jobImageId, and error info.
   */
  private async confirmOneFile(photo: JobPhotoItem): Promise<{ success: boolean; jobImageId?: number; errorMessage?: string; errorDetails?: string }> {
    if (!this.jobUploadSessionId || !photo.jobUploadSessionItemId) {
      return {
        success: false,
        errorMessage: 'Cannot confirm — missing session or item ID.',
        errorDetails: 'jobUploadSessionId or jobUploadSessionItemId was not set.'
      };
    }

    try {
      const res = await firstValueFrom(
        this.jobsService.confirmFile(
          this.jobId,
          this.jobUploadSessionId,
          this.selectedCompanyId(),
          photo.jobUploadSessionItemId
        )
      );

      if (res.data) {
        return {
          success: true,
          jobImageId: res.data.jobImageId
        };
      } else {
        return {
          success: false,
          errorMessage: res.message || 'Confirm failed.',
          errorDetails: `Server response: ${JSON.stringify(res)}`
        };
      }
    } catch (err: any) {
      return {
        success: false,
        errorMessage: err?.message || 'Failed to confirm upload.',
        errorDetails: `Error stack/details: ${err?.stack || err?.toString()}`
      };
    }
  }

  /**
   * Called when the upload queue finishes (all attempts done, success or fail).
   */
  private handleQueueComplete(): void {
    const stillFailed = this.photos.filter(p => p.uploadStatus === 'failed').length;
    const allDone = this.photos.every(
      p => p.uploadStatus === 'confirmed' || p.uploadStatus === 'skipped' || p.uploadStatus === 'duplicate_skipped'
    );

    if (allDone) {
      this.updateState(() => {
        this.isUploading = false;
        this.recalculateSummaryInternal();
      });
      this.onCompleteUpload();
    } else if (stillFailed > 0) {
      if (this.autoRetryCount < 3) {
        const nextAttempt = this.autoRetryCount + 1;
        this.startDelayedRetry(nextAttempt);
      } else {
        this.updateState(() => {
          this.isUploading = false;
          this.recalculateSummaryInternal();
        });
        this.handleError(
          `${stillFailed} file(s) failed to upload. Click "Resume Upload" to try them again.`
        );
      }
    } else {
      this.updateState(() => {
        this.isUploading = false;
        this.recalculateSummaryInternal();
      });
    }
  }

  private startDelayedRetry(nextAttempt: number): void {
    this.clearRetryTimer();

    let secondsLeft = 5;
    const tick = () => {
      if (secondsLeft > 0) {
        this.updateState(() => {
          this.isUploading = true;
          this.uploadStatusMessage = `Retrying failed uploads in ${secondsLeft} second${secondsLeft > 1 ? 's' : ''} (attempt ${nextAttempt}/3)…`;
        });
        secondsLeft--;
        this.retryTimerId = setTimeout(tick, 1000);
      } else {
        this.updateState(() => {
          this.autoRetryCount = nextAttempt;
        });
        this.runRetryFlow();
      }
    };

    tick();
  }

  private clearRetryTimer(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Attempts to refresh the SAS URL for a single photo.
   * Returns true if successful.
   */
  private async refreshSasForPhoto(photo: JobPhotoItem): Promise<boolean> {
    if (!this.jobUploadSessionId) return false;
    try {
      const res = await firstValueFrom(
        this.jobsService.refreshUploadUrls(this.jobId, this.jobUploadSessionId)
      );
      const match = res.data?.items.find(
        i => i.jobUploadSessionItemId === photo.jobUploadSessionItemId
          || i.clientFileId === photo.clientFileId
      );
      if (match?.uploadUrl) {
        photo.uploadUrl = match.uploadUrl;
        photo.uploadUrlExpiresAt = match.uploadUrlExpiresAt ?? null;
        return true;
      }
    } catch { /* ignore */ }
    return false;
  }

  /**
   * PUT a file directly to an Azure Blob SAS URL using XMLHttpRequest
   * so we can track upload progress.
   */
  private putToAzureBlob(
    url: string,
    file: File,
    onProgress: (pct: number) => void
  ): Promise<{ success: boolean; errorCode?: string; errorMessage?: string; errorDetails?: string; statusCode?: number }> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();

      // Set timeout to 3 minutes (180,000 ms)
      xhr.timeout = 180000;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.floor((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        // Azure returns 201 Created on success
        const isOk = xhr.status === 201 || xhr.status === 200;
        if (isOk) {
          resolve({ success: true, statusCode: xhr.status });
        } else {
          resolve({
            success: false,
            errorCode: 'AZURE_UPLOAD_FAILED',
            errorMessage: `Azure upload failed with status ${xhr.status}`,
            errorDetails: `Status text: ${xhr.statusText || ''}\nResponse: ${xhr.responseText || ''}`,
            statusCode: xhr.status
          });
        }
      });

      xhr.addEventListener('error', (e) => {
        resolve({
          success: false,
          errorCode: 'AZURE_UPLOAD_NETWORK_ERROR',
          errorMessage: 'Network error during Azure upload.',
          errorDetails: `An error occurred during XMLHttpRequest send. Event: ${JSON.stringify(e)}`
        });
      });

      xhr.addEventListener('abort', (e) => {
        resolve({
          success: false,
          errorCode: 'AZURE_UPLOAD_ABORTED',
          errorMessage: 'Azure upload aborted.',
          errorDetails: `The upload request was aborted. Event: ${JSON.stringify(e)}`
        });
      });

      xhr.addEventListener('timeout', () => {
        resolve({
          success: false,
          errorCode: 'AZURE_UPLOAD_TIMEOUT',
          errorMessage: 'Azure upload timed out.',
          errorDetails: 'The upload exceeded the timeout limit of 180 seconds.'
        });
      });

      xhr.open('PUT', url, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
      xhr.send(file);
    });
  }

  /**
   * Compute SHA-256 hash of a File and return it as a lowercase hex string.
   * Falls back gracefully if SubtleCrypto is not available.
   */
  private async computeSha256Hex(file: File): Promise<string> {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback: use filename + size as a weak dedup key
      return `${file.name}-${file.size}`;
    }
  }

  /**
   * Split an array into chunks of at most `size`.
   */
  private chunk<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  /**
   * Return a Promise that resolves after ms milliseconds.
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get delay in milliseconds with exponential backoff and jitter.
   * Attempt is 0-indexed. Delays:
   * Attempt 0 -> 2000ms
   * Attempt 1 -> 5000ms
   * Attempt 2 -> 10000ms
   */
  private getRetryDelay(attempt: number): number {
    const baseDelays = [2000, 5000, 10000];
    const base = baseDelays[Math.min(attempt, baseDelays.length - 1)];
    // Add a small random jitter (up to 500ms +/-)
    const jitter = Math.floor(Math.random() * 1000) - 500;
    return Math.max(100, base + jitter);
  }

  /**
   * Logs a failed upload/prepare/confirm attempt to the backend.
   * This is best-effort and will never throw/block the main upload flow.
   */
  private async logUploadError(
    item: JobPhotoItem,
    errorCode: string,
    errorMessage: string,
    errorDetails?: string
  ): Promise<void> {
    if (!this.jobUploadSessionId) {
      console.warn('logUploadError: Cannot log error, missing jobUploadSessionId');
      return;
    }

    const payload: LogUploadErrorRequest = {
      jobImageId: item.jobImageId ?? 0,
      jobUploadSessionItemId: item.jobUploadSessionItemId ?? 0,
      originalFileName: item.fileName,
      errorCode,
      errorMessage,
      errorDetails: errorDetails || '',
      blobPath: item.uploadUrl ? this.extractBlobPath(item.uploadUrl) : ''
    };

    try {
      await firstValueFrom(
        this.jobsService.logUploadError(this.jobId, this.jobUploadSessionId, payload)
      );
    } catch (err: any) {
      console.warn('logUploadError: Failed to log error to backend API', err);
    }
  }

  /**
   * Helper to extract blob path from Azure SAS URL if available.
   */
  private extractBlobPath(url: string): string {
    try {
      const urlObj = new URL(url);
      const parts = urlObj.pathname.split('/');
      if (parts.length > 2) {
        return parts.slice(2).join('/');
      }
      return urlObj.pathname;
    } catch {
      return '';
    }
  }

  // ─── Finalisation ───────────────────────────────────────────────────────────

  private finalizeUpload(): void {
    this.updateState(() => {
      this.isUploading = true;
      this.uploadStatusMessage = 'Submitting job for processing…';
    });

    this.jobsService.submitJobProcessing(this.jobId).subscribe({
      next: () => {
        this.updateState(() => {
          this.isUploading = false;
          this.jobsService.clearAttachedFiles();
          this.jobProcessingStateService.clearState(this.jobId);
          this.jobProcessingStateService.clearActiveCreateJobId();
          this.jobsService.refreshJobs();
          this.router.navigate(['/app/jobs']);
        });
      },
      error: (err: any) => {
        this.updateState(() => {
          this.isUploading = false;
          this.recalculateSummaryInternal();
        });
        const serverMsg = err?.error?.message || err?.message;
        this.handleError(
          serverMsg || 'Images uploaded successfully, but failed to submit the job for processing. Please try completing it again.'
        );
      }
    });
  }

  // ─── UI helpers ─────────────────────────────────────────────────────────────

  closeAlert(): void {
    this.updateState(() => {
      this.showErrorAlert = false;
    });
  }

  private handleError(message: string): void {
    this.updateState(() => {
      this.errorMessage = message;
      this.showErrorAlert = true;
    });
  }
}